import { AvatarGroup, Badge, Box, Button, Stack, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import { useEffect, type ReactElement } from 'react';
import { type TaskList, type ListTask, statuses, type TaskStatus } from '../types';
import { actionErrorAndThrow, formatDateTime } from 'utilities';
import { TaskStatusTag } from '../TaskStatusTag';
import { UserAvatar } from 'components/UserAvatar';
import { useNavigate, useParams } from 'react-router';
import { EmbeddedUser } from 'modules/core/types';
import useSWR, { KeyedMutator } from 'swr';
import { AddTaskDialog } from './AddTaskDialog';
import { UpdateTaskDialog } from './UpdateTaskDialog';
import { useTaskDraggable, useTaskDragMonitor, TaskDropZone, type MovePayload } from './dnd';
import { controlCol } from 'styles/style.css';
import { DragHandle } from 'components/DND';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { api } from 'services';

import { toast } from 'react-toastify';
import { ContentPopoverButton } from '../ContentPopoverButton';
import { DeadlineDate } from 'components/DeadlineDate';

function groupTasksByStatus(tasks: ListTask[]): Record<TaskStatus, ListTask[]> {
  const grouped = statuses.reduce(
    (acc, status) => {
      acc[status] = [];
      return acc;
    },
    {} as Record<TaskStatus, ListTask[]>,
  );

  tasks.forEach((task) => {
    grouped[task.status].push(task);
  });

  return grouped;
}

function useTaksNavigation(tasks: ListTask[]): [task: ListTask | null, setTask: (id: number | null) => void] {
  const navigate = useNavigate();
  const { task_id: rawTaskId } = useParams();

  let task: ListTask | null = null;
  const taskId = rawTaskId ? parseInt(rawTaskId, 10) : null;
  task = tasks.find((t) => t.id === taskId) || null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: this is fine
  useEffect(() => {
    if (rawTaskId) {
      if (!task) {
        toast.warn('This task is not found.');
        navigate('..', { replace: true, relative: 'path' });
      }
    }
  }, [rawTaskId]);

  const setTask = (id: number | null) => {
    if (rawTaskId) {
      navigate(id !== null ? `../${id}` : '..', { relative: 'path' });
    } else {
      navigate(String(id));
    }
  };
  return [task, setTask];
}

function TaskRow({
  task: { id, name, status, assignees, due_date, topic, created_by, created_at, content },
  index,
  setTask,
}: {
  task: ListTask;
  index: number;
  setTask: (id: number | null) => void;
}): ReactElement {
  const { elementRef, handleRef, closestEdge, isDragging } = useTaskDraggable(id, status, index, false);

  return (
    <Tr key={id} ref={elementRef} style={{ position: 'relative' }}>
      <Td textAlign="center" flush>
        <DragHandle ref={handleRef} active={isDragging} />
        {closestEdge && <DropIndicator edge={closestEdge} />}
      </Td>
      <Td>
        <Stack direction="horizontal" align="center" my="-1" justify="between">
          <Box as="span" role="button" color="primary" onClick={() => setTask(id)}>
            {name}
          </Box>
          <ContentPopoverButton content={content} />
        </Stack>
      </Td>
      <Td>
        <UserAvatar user={created_by} size="xs" />
      </Td>
      <Td>{formatDateTime(created_at)}</Td>
      <Td>{due_date ? <DeadlineDate deadline={due_date} /> : '-'}</Td>
      <Td>
        <AvatarGroup size="xs">
          {assignees.map((u) => (
            <UserAvatar user={u} key={u.id} />
          ))}
        </AvatarGroup>
      </Td>
      <Td>
        <Badge count={topic.count} variant="secondary">
          <Button
            testId={`comments-${topic.object_key}`}
            size="xxs"
            icon="message"
            onClick={() => setTask(id)}
            variant="secondary"
          />
        </Badge>
      </Td>
    </Tr>
  );
}

type TasksContainerProps = {
  tasks: ListTask[];
  url: string;
  onTaskListUpdate: (taskList: TaskList) => void;
  mutate: KeyedMutator<ListTask[]>;
};

export function TasksContainer({ tasks, url, onTaskListUpdate, mutate }: TasksContainerProps): ReactElement {
  const { data } = useSWR<EmbeddedUser[]>({ method: 'GET', url: `${url}assignee_options/` });

  // fallbackData?
  const assigneeOptions = data || [];

  const [task, setTask] = useTaksNavigation(tasks);
  const groupedTasks = groupTasksByStatus(tasks);

  const handleTaskMove = async (moveData: MovePayload) => {
    const { source_id, status, target_id, type } = moveData;

    const updatedTasks = [...tasks];
    const sourceTaskIndex = updatedTasks.findIndex((task) => task.id === source_id);

    if (sourceTaskIndex === -1) return;

    const sourceTask = { ...updatedTasks[sourceTaskIndex], status: status };
    // Remove from original position
    updatedTasks.splice(sourceTaskIndex, 1);

    // Find target position and insert before/after
    const targetIndex = updatedTasks.findIndex((task) => task.id === target_id);
    if (targetIndex !== -1) {
      const insertIndex = type === 'after' ? targetIndex + 1 : targetIndex;
      updatedTasks.splice(insertIndex, 0, sourceTask);
    } else {
      // Fallback: add to end
      updatedTasks.push(sourceTask);
    }

    // Update UI immediately
    mutate(updatedTasks, { revalidate: false });

    const result = await api.request<
      {
        tasks: ListTask[];
        task_list: TaskList;
      },
      unknown
    >({ url: `${url}move/`, method: 'POST', body: moveData });
    if (result.ok) {
      mutate(result.data.tasks, { revalidate: false });
      onTaskListUpdate(result.data.task_list);
    } else {
      actionErrorAndThrow(result);
    }
  };

  useTaskDragMonitor(handleTaskMove);

  return (
    <Stack direction="vertical">
      <UpdateTaskDialog
        onClose={() => setTask(null)}
        url={url}
        task={task}
        assigneeOptions={assigneeOptions}
        onTaskListUpdate={onTaskListUpdate}
        mutate={mutate}
      />

      {statuses.map((status) => (
        <Stack direction="vertical" key={status}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <TaskStatusTag state={status} appearance="naked" />
            {status === 'PENDING' && (
              <AddTaskDialog
                url={url}
                assigneeOptions={assigneeOptions}
                onTaskListUpdate={onTaskListUpdate}
                mutate={mutate}
              />
            )}
          </Box>
          <Table size="sm" mb="3" fixed>
            <colgroup>
              <col width="36px" />
              <col />
              {/* author */}
              <col width="100px" />
              {/* created */}
              <col width="140px" />
              {/* Due date */}
              <col width="120px" />
              {/* Assignees */}
              <col width="160px" />
              <col width="46px" />
            </colgroup>
            <Thead>
              <Tr>
                <Th className={controlCol}></Th>
                <Th>Name</Th>
                <Th>Author</Th>
                <Th>Created</Th>
                <Th>Due date</Th>
                <Th>Assignees</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {groupedTasks[status].length === 0 ? (
                <TaskDropZone status={status} />
              ) : (
                groupedTasks[status].map((task, index) => (
                  <TaskRow task={task} index={index} key={task.id} setTask={setTask} />
                ))
              )}
            </Tbody>
          </Table>
        </Stack>
      ))}
    </Stack>
  );
}
